import React, { MutableRefObject, ReactNode, useRef } from "react";
import Konva from "konva";
import { Html } from "react-konva-utils";
import ElementsTraitsButtons from "./elements-traits-buttons";
import { AlignmentButton } from "./align-object-buttons";
import { measureNodesScreenSpace } from "frontend/utils/node-utils";
import { Floater } from "frontend/ui-components/floaters/floater";
import style from "./elements-toolbar.module.css";
import classNames from "classnames";
import { getElementTypeForId, isElementInGantt, isIdOfType, isOfTypes } from "../elements/canvas-elements-utils";
import BoundingBox from "frontend/geometry/bounding-box";
import * as icons from "frontend/icons/icons";
import { Button, ButtonGroup, DropdownButton } from "./toolbar-buttons";
import { useAtomValue, useSetAtom } from "jotai";
import { syncServiceAtom } from "state-atoms/general-atoms";
import { LockType } from "shared/datamodel/schemas/canvas-element";
import consts, { TypeCanvasElement } from "shared/consts";
import { SubmenuDataUpdate } from "frontend/canvas-designer-new/canvas-submenu";
import { parseNodeId } from "../elements/org-chart/orgchart-utils";
import { selectedElementIdsAtom, tableSelectionAtom, TableSelectionState } from "state-atoms";
import * as R from "rambda";
import { TableCellKey, TableIds, useTableData } from "../elements/table/table-utils";
import { TableCellsInfo } from "../elements/table/table-info";
import { isRectangle, makeSelectionHelper } from "../elements/table/table-selection";
import { DeleteButton } from "./widgets/table/table-structure-widgets";
import { ReadTransaction } from "@workcanvas/reflect";
import { produceWithPatches } from "immer";
import { TypeTableElement } from "shared/datamodel/schemas/table";
import { canvasElementPrefix } from "shared/util/utils";
import { Reflect } from "@workcanvas/reflect/client";
import { M } from "shared/datamodel/mutators";
import UndoRedoStack from "utils/undo-redo/undo-redo-stack";
import { CheckoutSources } from "frontend/billing/checkout";
import { FiltersSelection } from "frontend/icons/orgchart-filters-selection";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import tracking from "frontend/tracking";
import { useConvertToMonday } from "../integrations/convert-to-monday/logic/use-convert-to-monday";
import { displayNameForFilterables, ICON_COLOR, ICON_SIZE } from "./consts";
import { ConvertToMondayItemButton } from "../integrations/convert-to-monday/ui/toolbar/convert-to-monday-item-button";
import { useDynamicElementActions } from "../element-dynamic-data/hooks/use-dynamic-element-actions";
import { cordEnabled } from "frontend/hooks/use-cord";

import { CustomizeMondayItemButton } from "../element-dynamic-data/toolbar/customize-monday-item-button";
import { AIEditsPanel } from "../ai-generator/ai-edits-panel/ai-edits-panel";

interface BaseToolbarProps {
  customColors: string[];
  elements: any[];
  tableCells: Map<string, any>;
  setNodePosition: (id: string, pos: { x: number; y: number }) => void;
  onMutationEnd: (e: any) => void;
  doGroup?: () => void;
  doUngroup?: () => void;
  doLock: (nodesOrIds?: Iterable<Konva.Node> | string[]) => void;
  doUnlock: (nodesOrIds?: Iterable<Konva.Node> | string[]) => void;
  onContextMenu?: (rect: any, nodes: Konva.Node[], locked: boolean) => void;
  mutateElementsProperties: (
    info: Map<string, any>,
    undoInfo: Map<string, any>,
    subMenuData?: SubmenuDataUpdate,
    addUndo?: boolean
  ) => void;
  selectedIdsRef: MutableRefObject<string[]>;
  onSelectionChanged: () => void;
  onCommentCreate: null | ((node: Konva.Node) => void);
  documentId?: string;
  reflect?: Reflect<M> | null;
  undoRedoStack: UndoRedoStack;
  showUpgradeModal: ((reason: CheckoutSources) => void) | undefined;
  onError?: (error: ReactNode | null) => void;
  onOpenDataLayerMenu: ({ elementId, anchor }: { elementId: string; anchor: DOMRectReadOnly }) => void;
  onOpenRulesMenu: ({ elementId, anchor }: { elementId: string; anchor: DOMRectReadOnly }) => void;
  onConvertToMondayItem?: (itemType: "item" | "subitem", nodes: Konva.Node[], anchor: DOMRect) => void; // TODO: Remove in the future in favor of context.
}

type ElementsToolbarProps = BaseToolbarProps & {
  elementsLayer: Konva.Layer | null;
};

type ElementsToolbarPropsNoNull = BaseToolbarProps & {
  ids: string[];
  elements: any[];
  nodes: Konva.Node[];
  customColors: string[];
};

const isOrgChartNode = (id: string) =>
  isIdOfType(id, consts.CANVAS_ELEMENTS.ORG_CHART_NODE) || isIdOfType(id, consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART);

const isFilterable = isOfTypes(
  ...Object.entries(displayNameForFilterables)
    .filter(([_key, value]) => value !== null)
    .map(([key]) => key as TypeCanvasElement)
);

const nonFilterableType = R.complement(isFilterable);

const groupItemsByType = R.groupBy(getElementTypeForId); // [ids...] -> { [type]: [ids for that type] }
const groupFilterablesByType = R.pipe(R.reject(nonFilterableType), groupItemsByType);
const sortEntriesByValueLengthAsc = R.sortBy(([_a, b]) => -b.length);

export default function ElementsToolbar({ elementsLayer, ...props }: ElementsToolbarProps): JSX.Element | null {
  const {
    selectedIdsRef,
    mutateElementsProperties,
    documentId,
    reflect,
    undoRedoStack,
    customColors,
    elements,
    tableCells,
  } = props;
  // Atoms
  const tableSelection = useAtomValue(tableSelectionAtom);

  const allIds = selectedIdsRef.current; // why not pass internalSelection from outside, like selectedIds?
  const idsToFetchFromDB = allIds.map((id: string) =>
    id.startsWith(consts.CANVAS_ELEMENTS.ORG_CHART_NODE) ? parseNodeId(id)!.fullElementId : id
  );

  // get sub-elements (table cells); can also get orgchart nodes here
  const isTable = isOfTypes("table");
  const idsJustOfTables = idsToFetchFromDB.filter(isTable);

  // tableCells is an array, one entry per table selected. That entry is an array of [key, value] pairs of cells
  // if mutating tables, go over their table cells, mutate them also

  const onChange: typeof mutateElementsProperties = (info, undoInfo, submenudata, addUndo) => {
    let extras: any[] | null = null;
    const pickTableCellProps = R.pick(["fill", "textColor", "font", "fontSize", "fontProps", "align", "valign"]);
    const trimPrefix = R.replace(/^cElement-/, "");

    for (const [key, value] of info.entries()) {
      //get props from value that are relevant for table cell
      const pickedProps = pickTableCellProps(value);
      if (isTable(key)) {
        const i = idsJustOfTables.indexOf(key);
        extras ??= [];
        const value = Array.from(tableCells.values())[i];
        // Ensure tableCellsForTable is always an array of [k, v] pairs
        const tableCellsForTable = Array.isArray(value) ? value : Object.entries(value ?? {});
        extras = extras.concat(
          tableCellsForTable
            .filter(([_, v]) => v && typeof v === "object")
            .map(([k, v]: [string, any]) => [k, { ...v, ...pickedProps }])
        );
        tableCellsForTable.forEach(([k, v]: [string, any]) => {
          if (v && typeof v === "object") {
            undoInfo.set(trimPrefix(k), pickTableCellProps(v));
          }
        });
      }
    }

    if (extras?.length) {
      for (const [key, value] of extras) {
        info.set(trimPrefix(key), value);
      }
    }
    mutateElementsProperties(info, undoInfo, submenudata, addUndo);
  };

  if (!elementsLayer) {
    return null;
  }
  // suppose I read elements from db, return them in object {key:value}, and read even some more, like table-cells
  // I pass the next toolbar the ids of the elements to take into account, but all the data is there. so it can be used
  if (tableSelection?.cells?.length) {
    return (
      <ToolbarTableCells
        tableSelection={tableSelection}
        elementsLayer={elementsLayer}
        customColors={customColors}
        mutateElementsProperties={mutateElementsProperties}
        documentId={documentId}
        reflect={reflect}
        undoRedoStack={undoRedoStack}
      />
    );
  }

  // break early if we didn't get data from DB, or if it's not the data we requested
  if (!elements) {
    return null;
  }

  const nodesCollection = elementsLayer
    .find((i: any) => allIds.includes(i.attrs.id) && !i.attrs.isFrameTitle)
    .toArray();

  // now synchronize the lists, discard elements that are not in the layer
  const ids: string[] = [],
    filteredElements: any[] = [],
    nodes: Konva.Node[] = [];

  // Helper function to find element for any node type
  const findElementForNode = (node: any, nodeId: string) => {
    // Mindmap child node - find root element
    if (node.attrs.type === "mindmap" && node.attrs.element?.rootId) {
      const rootId = node.attrs.element.rootId;
      return elements.find((e) => `${e.type}-${e.id}` == rootId);
    }

    // Org chart child node - use embedded element data
    if (nodeId.includes("orgChart") && node.attrs.element) {
      return { value: node.attrs.element };
    }

    // Regular element lookup
    return elements.find((e) => `${e.type}-${e.id}` == nodeId);
  };

  // Helper function to get element data for traits
  const getElementDataForTraits = (node: any, element: any) => {
    // For mindmap nodes, use node's own element data
    if (node.attrs.type === "mindmap" && node.attrs.element) {
      return { ...node.attrs.element, id: node.attrs.id, elementType: node.attrs.type };
    }

    // For all other elements, use element.value
    return { ...element.value, id: node.attrs.id, elementType: node.attrs.type };
  };

  for (const allId of allIds) {
    const node = nodesCollection.find((x) => x.attrs.id == allId);
    if (!node) continue;

    // Process all other nodes
    const element = findElementForNode(node, allId);

    if (element) {
      ids.push(allId);
      filteredElements.push(getElementDataForTraits(node, element));
      nodes.push(node);
    } else {
      // it's possible not to find monday cards that are inside a live-card-stack
    }
  }

  // ofirc: adding table cells to the mix, because to show the traits of a table you have to look
  // at all its cells
  tableCells.forEach((data, id) => {
    if (selectedIdsRef.current.some((selectedId: string) => id.includes(selectedId.replace(/table-/, "")))) {
      ids.push(id.replace(/cElement-/, ""));
      filteredElements.push(data);
    }
  });

  return (
    <ElementsToolbarNoNull
      {...props}
      ids={ids}
      elements={filteredElements}
      nodes={nodes}
      customColors={customColors}
      mutateElementsProperties={onChange}
      selectedIdsRef={selectedIdsRef}
      documentId={documentId}
      reflect={reflect}
      undoRedoStack={undoRedoStack}
    />
  );
}

function ElementsToolbarNoNull({
  ids,
  elements,
  nodes,
  customColors,
  setNodePosition,
  mutateElementsProperties,
  onMutationEnd,
  doGroup,
  doUngroup,
  doLock,
  doUnlock,
  onContextMenu,
  selectedIdsRef,
  onSelectionChanged,
  onCommentCreate,
  documentId,
  reflect,
  undoRedoStack,
  showUpgradeModal,
  onOpenDataLayerMenu,
  onConvertToMondayItem,
  onOpenRulesMenu,
}: ElementsToolbarPropsNoNull) {
  const dotsRef = useRef<any>(null);
  const setSelected = useSetAtom(selectedElementIdsAtom);

  const isDataLayerEnabled = useFeatureFlag("element-data-layer");
  const isAiEditsEnabled = useFeatureFlag("ai-edits");
  const { getDynamicElementForSelection } = useDynamicElementActions();

  const aiEditTypes = new Set(consts.AI_EDITS_ALLOWED_ELEMENT_TYPES);
  const showAiButton =
    isAiEditsEnabled &&
    selectedIdsRef.current.length > 0 &&
    ids.every((id) => aiEditTypes.has(getElementTypeForId(id)));
  const enableAiButton = showAiButton && elements.every((element) => element?.text);

  const totalOrgChartNodes = ids.reduce((acc, cur) => acc + +isOrgChartNode(cur), 0);
  const hasOrgChartNodeInSelection = totalOrgChartNodes > 0;
  const allAreOrgChartNodes = totalOrgChartNodes == ids.length;

  const isTaskCardInsideGantt = elements.some((element) => isElementInGantt(element, true));

  const locked = elements.filter(Boolean).some((elt) => (elt.lock ?? 0) != LockType.None);

  const hasFrameInSelection = ids.some((id) => isIdOfType(id, consts.CANVAS_ELEMENTS.FRAME));
  const hasGanttInSelection = ids.some((id) => isIdOfType(id, consts.CANVAS_ELEMENTS.GANTT));

  const total = BoundingBox.expandOnAll(measureNodesScreenSpace(nodes, { skipStroke: true }));
  const allowAlignment =
    selectedIdsRef.current.length > 1 && !doUngroup && !hasFrameInSelection && !hasOrgChartNodeInSelection;

  const dataLayerNode = getDynamicElementForSelection(nodes);
  const dataLayerElement = dataLayerNode ? elements.find((element) => element.id === dataLayerNode?.id()) : null;
  const isDataLayerElement = !!dataLayerElement;
  const isDataLayerElementConnected =
    isDataLayerElement && Object.keys(dataLayerElement.dataLayer?.integrations ?? {}).length > 0;
  const isSingleMondayItem = elements.length === 1 && isIdOfType(ids[0], consts.CANVAS_ELEMENTS.INTEGRATION);
  const showCustomizeMondayItemButton =
    (isSingleMondayItem || isDataLayerElementConnected) && isDataLayerEnabled && documentId;

  const {
    visible: showConvertToMondayButton,
    enabled: isConvertToMondayEnabled,
    message: convertToMondayTooltip,
  } = useConvertToMonday(nodes);

  function renderCommentButton() {
    if (!cordEnabled()) {
      return null;
    }
    if (elements.length == 1 && onCommentCreate != null) {
      return (
        <ButtonGroup>
          <Button
            onClick={() => {
              onCommentCreate(nodes[0]);
            }}
            data-testid="commentbutton"
            tooltip={consts.CONTEXT_MENU_STAGE.ADD_COMMENT}
          >
            <icons.Comment color={"white"} size={16} />
          </Button>
        </ButtonGroup>
      );
    }
    return null;
  }

  const unlockedItemsToolbar = () => (
    <>
      {showCustomizeMondayItemButton && <CustomizeMondayItemButton node={nodes[0]} documentId={documentId} />}
      {showConvertToMondayButton && (
        <ConvertToMondayItemButton
          nodes={nodes}
          enabled={isConvertToMondayEnabled}
          onConvertToMondayItem={onConvertToMondayItem}
          tooltip={convertToMondayTooltip}
        />
      )}
      {isDataLayerEnabled && isDataLayerElementConnected && (
        <div className={style.row}>
          <Button
            onClick={() => {
              const bbox = BoundingBox.expandOnAll(measureNodesScreenSpace(nodes, { skipStroke: true }));
              const anchor = new DOMRect(bbox.x, bbox.y, bbox.width, bbox.height);
              tracking.trackAnalyticsEvent("data-layer-panel-opened");
              onOpenDataLayerMenu({ elementId: dataLayerElement.id, anchor });
            }}
            data-testid="editDataButton"
            tooltip="Display data"
          >
            <icons.DataLayer fill="none" color={ICON_COLOR} size={ICON_SIZE} />
          </Button>
        </div>
      )}
      {isDataLayerEnabled && isDataLayerElement && (
        <div className={style.row}>
          <Button
            onClick={() => {
              const bbox = BoundingBox.expandOnAll(measureNodesScreenSpace(nodes, { skipStroke: true }));
              const anchor = new DOMRect(bbox.x, bbox.y, bbox.width, bbox.height);
              tracking.trackAnalyticsEvent("rules-panel-opened");
              onOpenRulesMenu({ elementId: dataLayerElement.id, anchor });
            }}
            data-testid="editRulesButton"
            tooltip="Style by rules"
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <icons.DataLayerRulesEditor fill="none" color={ICON_COLOR} size={ICON_SIZE} />
              <span
                style={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  lineHeight: "17.941px",
                  letterSpacing: "0.174px",
                }}
              >
                Style
              </span>
            </div>
          </Button>
        </div>
      )}
      <ElementsTraitsButtons
        ids={ids}
        elements={elements}
        mutateElementsProperties={mutateElementsProperties}
        customColors={customColors}
        documentId={documentId}
        reflect={reflect}
        undoRedoStack={undoRedoStack}
        showUpgradeModal={showUpgradeModal}
        selectedIds={selectedIdsRef.current}
      />
      {allowAlignment && (
        <div className={style.row}>
          <AlignmentButton nodes={nodes} setNodePosition={setNodePosition} onMutationEnd={onMutationEnd} />
        </div>
      )}
      {((doGroup && ids.length > 1) || doUngroup) &&
        !hasFrameInSelection &&
        !hasOrgChartNodeInSelection &&
        !isDataLayerElement && (
          <ButtonGroup>
            {doGroup && (
              <Button onClick={() => doGroup!()} data-testid="Group" tooltip="Group">
                <icons.Group color={ICON_COLOR} size={ICON_SIZE} />
              </Button>
            )}
            {doUngroup && !isDataLayerElement && (
              <Button onClick={() => doUngroup!()} data-testid="Ungroup" tooltip="Ungroup">
                <icons.Ungroup color={ICON_COLOR} size={ICON_SIZE} />
              </Button>
            )}
          </ButtonGroup>
        )}
      {renderCommentButton()}
      {!hasOrgChartNodeInSelection && !isTaskCardInsideGantt && (
        <ButtonGroup>
          <Button onClick={() => doLock!(ids)} data-testid="lockbutton" tooltip="Lock">
            <icons.Unlock color={ICON_COLOR} size={ICON_SIZE} />
          </Button>
        </ButtonGroup>
      )}
      {showAiButton && (
        <div>
          <DropdownButton
            tooltip="AI"
            hideArrow
            editable={enableAiButton}
            renderPopup={(closePopup: () => void) => {
              return (
                <AIEditsPanel
                  onClose={() => {
                    closePopup();
                  }}
                  selectedElementIds={selectedIdsRef.current}
                />
              );
            }}
          >
            <icons.VisalizeBoardIcon />
          </DropdownButton>
        </div>
      )}
    </>
  );

  // -- preparing code for "filter" button
  // Filtering elements is actually selecting a subset of the current selection, by element-type
  // We allow it when we have groups (more than 1 element), and at least one of them is filterable, and they're
  // not all of the same type
  const showFilter =
    ids.length > 1 && ids.some((id) => isFilterable(id) && getElementTypeForId(id) != getElementTypeForId(ids[0]));
  let stats: [string, string[]][] | undefined;

  if (showFilter) {
    const byType = groupFilterablesByType(ids);
    if (byType[consts.CANVAS_ELEMENTS.MINDMAP]) {
      // each mindmap node is counted as element.
      // I should only count the root of the mindmap.
      byType[consts.CANVAS_ELEMENTS.MINDMAP] = elements
        .filter((e) => e.type === consts.CANVAS_ELEMENTS.MINDMAP && !e.parentId)
        .map((e) => e.rootId);
    }
    stats = Object.entries(byType);
  }

  const shouldLockPosition = hasGanttInSelection && !!(document.activeElement as HTMLDivElement)?.dataset?.lockToolbar;

  return (
    <Html transform={false}>
      <Floater
        relativeTo={total}
        arrowSize={0}
        margin={65}
        boundary="no-controls-area"
        className={classNames(style.mainToolbar, style.withSeparators)}
        lockPosition={shouldLockPosition}
        role="toolbar"
      >
        {stats && (
          <DropdownButton
            side="bottom"
            arrowSize={12}
            tooltip="Filter by object type"
            className={style.miniToolbarPopup}
            renderPopup={function (closePopup: () => void): React.ReactNode {
              return (
                stats && (
                  <table className={style.filterShapeTable}>
                    {sortEntriesByValueLengthAsc(stats).map(([key, value]) => (
                      <tr
                        key={key}
                        onClick={() => {
                          selectedIdsRef.current = value;
                          onSelectionChanged();
                          setSelected(value);
                          closePopup();
                        }}
                      >
                        <td>{value.length}</td>
                        <td>{displayNameForFilterables[key as TypeCanvasElement]}</td>
                      </tr>
                    ))}
                  </table>
                )
              );
            }}
          >
            <FiltersSelection />
          </DropdownButton>
        )}
        {locked ? (
          <>
            {renderCommentButton()}
            {!isTaskCardInsideGantt && (
              <Button onClick={() => doUnlock!(ids)} data-testid="unlockbutton" tooltip="Unlock">
                <icons.Lock color={ICON_COLOR} size={ICON_SIZE} />
              </Button>
            )}
          </>
        ) : (
          unlockedItemsToolbar()
        )}
        {!allAreOrgChartNodes && (
          <ButtonGroup>
            <Button
              data-testid="itemsubmenu"
              ref={dotsRef}
              onClick={() => {
                onContextMenu && onContextMenu(dotsRef.current?.getBoundingClientRect(), nodes, locked!);
              }}
            >
              <icons.ThreeDots size={ICON_SIZE} color={ICON_COLOR} />
            </Button>
          </ButtonGroup>
        )}
      </Floater>
    </Html>
  );
}

//TODO: copied from table-element.tsx

interface OrderedMap<T> {
  order: string[];
  values: Record<string, T>;
}

function buildOrderedMap(data: { id: string; size: number }[], scale = 1): OrderedMap<number> {
  const order = [];
  const values: Record<string, number> = {};
  for (const { id, size } of data) {
    order.push(id);
    values[id] = size * scale;
  }
  return { order, values };
}

function accumulate(v: OrderedMap<number>, accumulator: (prev: number, cur: number) => number) {
  let prev;
  for (const id of v.order) {
    if (prev) {
      v.values[id] = accumulator(v.values[prev], v.values[id]);
    }
    prev = id;
  }
  return v;
}

function calcGridLines(element: TypeTableElement) {
  const xs = accumulate(buildOrderedMap(element.cols, element.scaleX), R.add);
  const ys = accumulate(buildOrderedMap(element.rows, element.scaleY), R.add);
  return { xs: xs.values, ys: ys.values };
}

/**
 * Toolbar for selection of table cells only
 * @param param0
 * @returns
 */
function ToolbarTableCells({
  tableSelection,
  elementsLayer,
  customColors,
  mutateElementsProperties,
  documentId,
  reflect,
  undoRedoStack,
}: {
  tableSelection: TableSelectionState;
  elementsLayer: Konva.Layer;
  customColors: string[];
  mutateElementsProperties: (
    info: Map<string, any>,
    undoInfo: Map<string, any>,
    subMenuData?: SubmenuDataUpdate,
    addUndo?: boolean
  ) => void;
  documentId?: string;
  reflect?: Reflect<M> | null;
  undoRedoStack: UndoRedoStack;
}) {
  // Hooks
  const syncService = useAtomValue(syncServiceAtom);
  const rep = syncService?.getReplicache();

  const keyChain = new TableIds(tableSelection.id);
  const tableId = keyChain.fullTableId();
  const { element, cells } = useTableData(rep, tableId);

  // ---- All hooks above this line, below it can return null ----
  // empty element means we got the default values from useTableData, and we're waiting for the actual data
  if (!element.lastModifiedTimestamp || !keyChain) {
    return null;
  }

  // if the table is locked, cells have no toolbar
  if (element.lock === LockType.Full) {
    return null;
  }

  // To handle merged cells we need to consider these scenarios:
  // 1. User clicked on merged-cell: we get the id of the merge root, and we want to style the entire group
  // 2. User moved with keyboard into merged-cell: we get the id of a merged-cell (span=0), and we want to style the entire group
  //
  // We can solve both issues with this rule: if cell[id].mergedTo!='', collect all cells with same mergedTo
  // So if we have 1 id of a invisible (span==0) cell, we replace with its 'mergedTo'
  // Then for all ids of merged cells with span>1, we collect their entire group.

  // Our cells object is keyed by full-ids, so I want to work with those.
  const shortIds = tableSelection.cells!.map(({ col, row }) => keyChain.shortId(col, row));
  const fullIds = tableSelection.cells!.map(({ col, row }) => keyChain.longId(col, row));

  const fullIdsSet = new Set<string>(),
    collectTheseGroups = new Set<string>();

  for (const fullId of fullIds) {
    if (cells[fullId]?.mergedTo) {
      collectTheseGroups.add(cells[fullId].mergedTo!);
    }
  }
  for (const groupId of collectTheseGroups) {
    for (const [id, cell] of Object.entries(cells)) {
      if (cell.mergedTo == groupId) {
        fullIdsSet.add(id);
      }
    }
  }

  // When displaying the table the font is scaled, so actual font size is font size * scale.
  // To show the traits of the cells, we need to know the scale of the table.
  // So we prepare a function that will add to each cell the scale of the table, and the toolbar will show the true font-size.
  const addExtraDataForCells = (cell: any) => R.merge(cell, { scaleX: element.scaleX, scaleY: element.scaleY });

  // This part is a pipeline that gets just the cells that are in the selection, and adds the scale to them.
  //prettier-ignore
  const justRelevantCells = R.pipe(
    R.filter((_: any, key: string) => fullIds.includes(key)),  // filter to keep only cells that are in the selection
    R.values,                                                  // get just values (actual data)
    R.map(addExtraDataForCells)                              // add the scale to the cell
  )(cells);

  // check if ids is a set of columns/rows, but not entire table, and if so show the "remove col/row" buttons
  const helper = new TableCellsInfo(element.cols, element.rows, [], []);
  // Todo: all these checks can be very easy if we build a grid of 1/0 of the selected cells.
  const sel = makeSelectionHelper(tableId, element, tableSelection);
  const selectedCols = sel.getFullySelectedColumns(); // getColumnsThatAreFullySelected(selectedCellsSet, helper);
  const selectedRows = sel.getFullySelectedRows(); //getRowsThatAreFullySelected(selectedCellsSet, helper);
  const rectangular = isRectangle(tableSelection.cells, element, tableSelection.id, cells);

  // Check if we allow merge/unmerge:
  // unmerge - only if only merged-cells are selected, equivalent to:  no cells with mergedTo='' or undefined
  // merge - if the selection is rectangular and the selection isn't a single merged group
  const allowUnmerge = fullIds.every((id) => cells[id] && cells[id].mergedTo != undefined && cells[id]?.mergedTo != "");
  const theMergesInSelection = fullIds.reduce((acc, cur) => {
    acc.add(cells[cur]?.mergedTo || "");
    return acc;
  }, new Set<string>());

  let selectionDoesntIncludeAllMergedCells = false;
  for (const id of theMergesInSelection) {
    if (id === "") continue;
    // check that all cells in the merge group 'id' are in fullIds
    for (const [key, cell] of Object.entries(cells)) {
      if (cell.mergedTo == id && !fullIds.includes(key)) {
        selectionDoesntIncludeAllMergedCells = true;
        break;
      }
    }
    if (selectionDoesntIncludeAllMergedCells) break;
  }

  // We merge only N>1 cells, that form a rectangle.
  // They can be all regular cells, but if some are already merged, make sure their entire merged group is selected
  // Also check not all cells are in the same group already (we can't merge a merged-cell)
  const allowMerge = rectangular && (theMergesInSelection.size > 1 || theMergesInSelection.has(""));
  // Find the graphic nodes so we know where to place the toolbar
  const nodes = elementsLayer
    .find((i: Konva.Node) => shortIds.includes(i.attrs.id) && !i.attrs.isFrameTitle)
    .toArray()
    .filter(Boolean);
  const total = BoundingBox.expandOnAll(measureNodesScreenSpace(nodes, { skipStroke: true, skipShadow: true }));

  async function onRemoveColsRows() {
    // Deleting col/row means to do the following:
    // 1. delete the col/row from the table-element
    // 2. delete elements contained in the col/row
    // 3. shift left/up other elements in the table
    //
    // Check we're not trying to delete merged-cells
    // Ideally, the button will be disabled if we don't allow
    //TODO: if all cols/rows of the merge cell are selected, we should allow. check for this

    if (!rep) return; // just so compiler won't complain that rep is not defined

    //Note: it would be so much easier if I could just send a partial new state layer above,
    // and it would apply it with a deep-merge, and calculate the undo (diff) automatically.
    const changeSet = new ChangeSet();

    // We'll start with deleting elements
    // collect and flatten all containedIds arrays in the cells
    const containedElementsIds = justRelevantCells.flatMap(R.prop("containedIds")).filter(Boolean) as any[] as string[];

    const containedElementsToDelete = await rep.query((tx: ReadTransaction) =>
      Promise.all(containedElementsIds.map((id) => tx.get("cElement-" + id)))
    );
    containedElementsToDelete.forEach((v: any, index: number) => {
      if (v && !v.hidden) {
        changeSet.add(
          containedElementsIds[index],
          (draft) => {
            draft.hidden = true;
          },
          v
        );
      }
    });

    // Now remove the cols/rows from the table
    changeSet.add(
      tableId,
      (draft: TypeTableElement) => {
        if (selectedCols) draft.cols = draft.cols.filter(({ id }) => !selectedCols.includes(id));
        if (selectedRows) draft.rows = draft.rows.filter(({ id }) => !selectedRows.includes(id));
      },
      element
    );

    // calculate the new grid lines of the table, and shift the contained elements that should move
    const changedTable = changeSet.get(tableId);
    const originalGridLines = calcGridLines(element);
    const newGridLines = calcGridLines(changedTable);

    // Make a list of contained-elements that need to shift (ids and delta):
    const idsOfElementsToShift: string[] = [];
    const deltas: [number, number][] = [];

    for (const { id: col } of changedTable.cols) {
      for (const { id: row } of changedTable.rows) {
        const x1 = originalGridLines.xs[col],
          y1 = originalGridLines.ys[row];
        const x2 = newGridLines.xs[col],
          y2 = newGridLines.ys[row];
        const dx = x2 - x1,
          dy = y2 - y1;
        if (dx || dy) {
          const cellId = keyChain!.longId(col, row);
          const cell = cells[cellId];

          if (cell?.containedIds?.length) {
            for (const id of cell.containedIds) {
              idsOfElementsToShift.push(id);
              deltas.push([dx, dy]);
            }
          }
        }
      }
    }

    if (idsOfElementsToShift.length) {
      const elements = await rep.query(async (tx: ReadTransaction) =>
        Promise.all(idsOfElementsToShift.map((id) => tx.get(TableIds.ensureFullId(id))))
      );
      elements.forEach((v: any, index: number) => {
        if (v && !v.hidden) {
          changeSet.add(
            idsOfElementsToShift[index],
            (draft) => {
              draft.x += deltas[index][0];
              draft.y += deltas[index][1];
            },
            v
          );
        }
      });
    }
    changeSet.withStupidOldApi(mutateElementsProperties);
    // Table state still might refer to the selection we just removed, but it can handle it.
  }

  function onMergeCells() {
    const info = new Map<string, any>(),
      undoInfo = new Map<string, any>();
    const { minX, minY, maxX, maxY } = rectangular!;
    // the width and height are not correct if the selected cells are adopted
    // they should be some of span for the selected cells
    // mark all selected cells with spanX=spanY=0, clear their text, copy style from top-left cell
    const col = helper.colKey(minX),
      row = helper.rowKey(minY),
      topLeftCellKey = `${col}-${row}`,
      topleftCell = cells[keyChain.longId(col, row)] ?? {};

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const c = helper.colKey(x);
        const r = helper.rowKey(y);
        const key = keyChain.shortId(c, r);
        const cellData = cells[keyChain.longId(c, r)] ?? {};
        const newData = { ...topleftCell, spanX: 0, spanY: 0, mergedTo: topLeftCellKey, text: "" };

        undoInfo.set(key, { spanX: 1, spanY: 1, mergedTo: "", ...cellData });
        info.set(key, newData);
      }
    }

    // now mark the top-left with spanX=width, spanY=height
    const key = keyChain.shortId(col, row);
    undoInfo.set(key, { spanX: 1, spanY: 1, mergedTo: "", ...topleftCell });
    const newData = { ...topleftCell, spanX: maxX - minX + 1, spanY: maxY - minY + 1, mergedTo: topLeftCellKey };
    info.set(key, newData);

    mutateElementsProperties(info, undoInfo, undefined, true);
  }

  function onUnmergeCells() {
    const info = new Map<string, any>(),
      undoInfo = new Map<string, any>();

    const mergeRoots = new Set(fullIds.map((id) => cells[id]?.mergedTo || ""));
    for (const mergeRoot of mergeRoots) {
      if (!mergeRoot) continue;
      for (const [key, cell] of Object.entries(cells)) {
        if (cell?.mergedTo == mergeRoot) {
          const parsedCellKey = TableCellKey.from(key);
          if (parsedCellKey) {
            const k = keyChain.shortId(parsedCellKey.col, parsedCellKey.row);
            undoInfo.set(k, cell);
            info.set(k, { ...cell, mergedTo: "", spanX: 1, spanY: 1 });
          }
        }
      }
    }

    mutateElementsProperties(info, undoInfo, undefined, true);
    // Table state still might refer to the selection we just removed, but it can handle it.
  }

  // Let's check if we can delete columns/rows
  // To delete a column (or row):
  // - the entire column has to be selected
  // - we can't select the entire table and select all the cells
  // - we can't delete in the middle of a merged cell groups
  let allowToDeleteColumns =
    selectedCols?.length && selectedCols.length !== helper.numColumns && !selectionDoesntIncludeAllMergedCells;

  let allowToDeleteRows =
    selectedRows?.length && selectedRows.length !== helper.numRows && !selectionDoesntIncludeAllMergedCells;

  return (
    <Html transform={false}>
      <Floater
        relativeTo={total}
        arrowSize={0}
        margin={65}
        boundary="no-controls-area"
        className={classNames(style.mainToolbar, style.withSeparators)}
      >
        <ElementsTraitsButtons
          ids={shortIds}
          elements={justRelevantCells} // TODO: make the component accept object
          mutateElementsProperties={mutateElementsProperties}
          customColors={customColors}
          documentId={documentId}
          reflect={reflect}
          undoRedoStack={undoRedoStack}
        />
        {allowToDeleteColumns && (
          <ButtonGroup>
            <DeleteButton
              tooltip={`Remove column${selectedCols!.length == 1 ? "" : "s"} from table`}
              onClick={onRemoveColsRows}
            />
          </ButtonGroup>
        )}

        {allowToDeleteRows && (
          <ButtonGroup>
            <DeleteButton
              tooltip={`Remove row${selectedRows!.length == 1 ? "" : "s"} from table`}
              onClick={onRemoveColsRows}
            />
          </ButtonGroup>
        )}
        {allowMerge && (
          <ButtonGroup>
            <Button onClick={onMergeCells} tooltip={"Merge"}>
              <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.1272 1H2.31862C1.5884 1 1 1.59198 1 2.32663V8.17049"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <path
                  d="M12.8728 1H18.6814C19.4086 1 20 1.59198 20 2.32663V8.17049"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <path
                  d="M12.8728 20H18.6814C19.4086 20 20 19.408 20 18.6734V12.8295"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <path
                  d="M8.1272 20H2.31862C1.59142 20 1 19.408 1 18.6734V12.8295"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <path
                  d="M2 9.9C1.66863 9.9 1.4 10.1686 1.4 10.5C1.4 10.8314 1.66863 11.1 2 11.1V9.9ZM2 11.1H9V9.9H2V11.1Z"
                  fill="white"
                />
                <g filter="url(#filter0_d_47_3)">
                  <path d="M7 8L9 10.5L7 13" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
                </g>
                <path
                  d="M19 11.1C19.3314 11.1 19.6 10.8314 19.6 10.5C19.6 10.1686 19.3314 9.9 19 9.9L19 11.1ZM19 9.9L12 9.9L12 11.1L19 11.1L19 9.9Z"
                  fill="white"
                />
                <g filter="url(#filter1_d_47_3)">
                  <path d="M14 13L12 10.5L14 8" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
                </g>
                <defs>
                  <filter
                    id="filter0_d_47_3"
                    x="2.39996"
                    y="7.39998"
                    width="11.3684"
                    height="14.2"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="4" />
                    <feGaussianBlur stdDeviation="2" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_47_3" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_47_3" result="shape" />
                  </filter>
                  <filter
                    id="filter1_d_47_3"
                    x="7.23163"
                    y="7.39998"
                    width="11.3684"
                    height="14.2"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="4" />
                    <feGaussianBlur stdDeviation="2" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_47_3" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_47_3" result="shape" />
                  </filter>
                </defs>
              </svg>
            </Button>
          </ButtonGroup>
        )}

        {allowUnmerge && (
          <Button onClick={onUnmergeCells} tooltip={"Unmerge"}>
            <svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.1272 1H4.31862C3.5884 1 3 1.59198 3 2.32663V8.17049"
                stroke="white"
                strokeWidth="1.2"
                strokeMiterlimit="10"
                strokeLinecap="round"
              />
              <path
                d="M14.8728 1H20.6814C21.4086 1 22 1.59198 22 2.32663V8.17049"
                stroke="white"
                strokeWidth="1.2"
                strokeMiterlimit="10"
                strokeLinecap="round"
              />
              <path
                d="M14.8728 20H20.6814C21.4086 20 22 19.408 22 18.6734V12.8295"
                stroke="white"
                strokeWidth="1.2"
                strokeMiterlimit="10"
                strokeLinecap="round"
              />
              <path
                d="M10.1272 20H4.31862C3.59142 20 3 19.408 3 18.6734V12.8295"
                stroke="white"
                strokeWidth="1.2"
                strokeMiterlimit="10"
                strokeLinecap="round"
              />
              <path
                d="M11 11.1C11.3314 11.1 11.6 10.8314 11.6 10.5C11.6 10.1686 11.3314 9.9 11 9.9L11 11.1ZM11 9.9L5 9.9L5 11.1L11 11.1L11 9.9Z"
                fill="white"
              />
              <g filter="url(#filter0_d_47_18)">
                <path d="M7 13L5 10.5L7 8" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
              </g>
              <path
                d="M14 9.9C13.6686 9.9 13.4 10.1686 13.4 10.5C13.4 10.8314 13.6686 11.1 14 11.1V9.9ZM14 11.1H20V9.9H14V11.1Z"
                fill="white"
              />
              <g filter="url(#filter1_d_47_18)">
                <path d="M18 8L20 10.5L18 13" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
              </g>
              <defs>
                <filter
                  id="filter0_d_47_18"
                  x="0.231628"
                  y="7.39998"
                  width="11.3684"
                  height="14.2"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_47_18" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_47_18" result="shape" />
                </filter>
                <filter
                  id="filter1_d_47_18"
                  x="13.4"
                  y="7.39998"
                  width="11.3684"
                  height="14.2"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_47_18" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_47_18" result="shape" />
                </filter>
              </defs>
            </svg>
          </Button>
        )}
      </Floater>
    </Html>
  );
}

class ChangeSet {
  public readonly patches: any[] = [];
  newElements: any = {};
  originalElements: any = {};

  add<T>(id: string, changeFunction: (draft: T) => void, element: T) {
    const fullId = id.startsWith(canvasElementPrefix) ? id : canvasElementPrefix + id;
    const base = this.newElements[fullId] ?? element;
    if (base == element) this.originalElements[fullId] = element;
    const [changedElement, patch, inversePatch] = produceWithPatches(changeFunction)(base);
    this.newElements[fullId] = changedElement;
    this.patches.push({ id: fullId, patch, inversePatch });
  }

  get(id: string) {
    const fullId = id.startsWith(canvasElementPrefix) ? id : canvasElementPrefix + id;
    return this.newElements[fullId];
  }

  withStupidOldApi(
    mutateElementsProperties: (
      info: Map<string, any>,
      undoInfo: Map<string, any>,
      subMenuData?: SubmenuDataUpdate,
      addUndo?: boolean
    ) => void
  ) {
    const info = new Map(),
      undo = new Map();
    for (const [id, element] of Object.entries(this.newElements)) {
      const shortId = id.slice(canvasElementPrefix.length);
      info.set(shortId, element);
      undo.set(shortId, this.originalElements[id]);
    }
    mutateElementsProperties(info, undo);
  }
}

export function allItemsBelongToSingleTable(selectedIds: string[]) {
  if (selectedIds.length === 0) return false;
  if (!TableIds.fromCellId(selectedIds[0])) return false;
  const [prefix] = selectedIds[0].split("/");
  return selectedIds.every((id) => id.startsWith(prefix));
}
